import { CopyOutlined, DeleteOutlined, DollarTwoTone, DownloadOutlined, EditOutlined, FileImageOutlined, SaveOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Drawer, Form, Image, Input, Modal, notification, Select, Upload } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  addDesglose,
  deleteDesglose,
  editDesglose,
  getDesglose,
} from "../../../../../context/actions/desgloseActions";
import { formatCurrency } from "../../../../../utils/formatCurrency";
import {
  opcionesISRRetenido,
  opcionesIVA,
  opcionesIVARetenido,
  opcionesQuienPaga,
  options,
} from "./DrawerDesgloseCostos.constants";
import styles from "./DrawerDesgloseCostos.module.css";

const formatAccounting = value => Number(value || 0).toLocaleString("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const impuestosIniciales = {iva: 16, ivaR: 0, isrR: 0};

const calcularImportes = item => {
  const subtotal = Number(item.cantidad || 0) * Number(item.costo || 0);
  const importeIVA = subtotal * Number(item.iva || 0) / 100;
  const importeIVARetenido = subtotal * Number(item.ivaR || 0) / 100;
  const importeISRRetenido = subtotal * Number(item.isrR || 0) / 100;

  return {
    subtotal,
    importeIVA,
    importeIVARetenido,
    importeISRRetenido,
    totalFiscal: subtotal + importeIVA - importeIVARetenido - importeISRRetenido,
  };
};

export const DrawerDesgloseCostos = ({open, setOpen}) => {
  const { asistenciaID } = useParams();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [preview, setPreview] = useState({});
  const [impuestosPorItem, setImpuestosPorItem] = useState({});
  const [documentUrl, setDocumentUrl] = useState("");
  const usuarioId = useSelector(state => state.userReducer.user.usuarioID);
  const coberturaTope = useSelector(state => state.detalleReducer.asistencia?.coberturaTope);
  const totalesPorPagador = items.reduce((totales, item) => {
    const importe = item.cantidad * item.costo;

    totales.total += importe;

    if (item.quienPaga in totales) {
      totales[item.quienPaga] += importe;
    }

    return totales;
  }, {
    total: 0,
    Escotel: 0,
    "Usuario Final": 0,
    "Cliente Corporativo": 0,
  });
  const totalEscotel = totalesPorPagador.Escotel;
  const totalFiscal = items.reduce((acumulado, item) => {
    const impuestos = impuestosPorItem[item.id] || {};
    return acumulado + calcularImportes({...impuestosIniciales, ...item, ...impuestos}).totalFiscal;
  }, 0);
  const totalesFiscalesPorPagador = items.reduce((totales, item) => {
    const impuestos = impuestosPorItem[item.id] || {};

    if (item.quienPaga in totales) {
      totales[item.quienPaga] += calcularImportes({...impuestosIniciales, ...item, ...impuestos}).totalFiscal;
    }

    return totales;
  }, {
    Escotel: 0,
    "Usuario Final": 0,
    "Cliente Corporativo": 0,
  });
  const previewImportes = calcularImportes({...impuestosIniciales, ...preview});
  const esCostoEscotel = preview.quienPaga === "Escotel";
  const esClienteCorporativo = preview.quienPaga === "Cliente Corporativo";
  const camposRequeridosIncompletos = !preview.tipoCosto || !preview.costo || !preview.cantidad || !preview.quienPaga;
  const excedeCobertura = esCostoEscotel && previewImportes.totalFiscal > 0 && totalesFiscalesPorPagador.Escotel + previewImportes.totalFiscal > coberturaTope;
  const totalExcedeCobertura = totalesFiscalesPorPagador.Escotel > coberturaTope;

  const loadDesglose = useCallback(async () => {
    const result = await getDesglose(asistenciaID);

    if (result.success) {
      setItems(result.data);
    } else {
      notification.error({className: styles.costNotification, message: result.error});
    }
  }, [asistenciaID]);

  useEffect(() => {
    if (open) {
      setItems([]);
      loadDesglose();
      return;
    }

    setItems([]);
    setEditingItem(null);
    form.resetFields();
    setPreview({});
    setImpuestosPorItem({});
  }, [form, loadDesglose, open]);

  const onValuesChange = (_, allValues) => {
    setPreview(allValues);
  };

  const onFinish = async values => {
    setIsLoading(true);

    try {
      const idsAnteriores = new Set(items.map(item => item.id));
      const result = await addDesglose({
        ...values,
        usuarioID: usuarioId,
        asistenciaID,
      });

      if (result.success) {
        notification.success({
          className: styles.costNotification,
          message: result.data,
        });
        form.resetFields();
        setPreview({});
        const refreshed = await getDesglose(asistenciaID);

        if (refreshed.success) {
          setItems(refreshed.data);
          const nuevosItems = refreshed.data.filter(item => !idsAnteriores.has(item.id));
          setImpuestosPorItem(actuales => nuevosItems.reduce((resultado, item) => ({
            ...resultado,
            [item.id]: {iva: values.iva, ivaR: values.ivaR, isrR: values.isrR},
          }), actuales));
        }
      } else {
        notification.error({className: styles.costNotification, message: result.error});
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onDelete = async (id) => {
    setIsLoading(true);

    try {
      const result = await deleteDesglose(id);

      if (result.success) {
        notification.success({
          className: styles.costNotification,
          message: result.data,
        });
        await loadDesglose();
      } else {
        notification.error({className: styles.costNotification, message: result.error});
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onEdit = async () => {
    setIsLoading(true);

    try {
      const result = await editDesglose({
        asistenciaID,
        id: editingItem.id,
        costo: editingItem.costo,
        cantidad: editingItem.cantidad,
        iva: editingItem.iva,
        ivaR: editingItem.ivaR,
        isrR: editingItem.isrR,
      });

      if (result.success) {
        notification.success({
          className: styles.costNotification,
          message: result.data,
        });
        setImpuestosPorItem(actuales => ({
          ...actuales,
          [editingItem.id]: {
            iva: editingItem.iva,
            ivaR: editingItem.ivaR,
            isrR: editingItem.isrR,
          },
        }));
        setEditingItem(null);
        await loadDesglose();
      } else {
        notification.error({className: styles.costNotification, message: result.error});
      }
    } finally {
      setIsLoading(false);
    }
  };

  const downloadDocument = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = "evidencia";
    link.click();
  };

  const openDocument = (url) => {
    if (/\.(jpe?g|png|gif|webp|bmp)(?:[?#]|$)/i.test(url)) {
      setDocumentUrl(url);
      return;
    }

    downloadDocument(url);
  };

  const copyCosts = async () => {
    const detail = items.map((item, index) => {
      const itemConImpuestos = {...impuestosIniciales, ...item, ...(impuestosPorItem[item.id] || {})};
      const importes = calcularImportes(itemConImpuestos);

      return [
        `${index + 1}. ${item.tipoCosto}`,
        `   ${formatAccounting(item.cantidad)} × $${formatAccounting(item.costo)} = $${formatAccounting(importes.subtotal)}`,
        `   IVA ${itemConImpuestos.iva}%: $${formatAccounting(importes.importeIVA)}`,
        `   IVA-R ${itemConImpuestos.ivaR}%: -$${formatAccounting(importes.importeIVARetenido)}`,
        `   ISR-R ${itemConImpuestos.isrR}%: -$${formatAccounting(importes.importeISRRetenido)}`,
        `   Total: $${formatAccounting(importes.totalFiscal)}`,
        `   Quién paga: ${item.quienPaga}`,
      ].join("\n");
    });
    const costs = [
      "COSTOS",
      `${asistenciaID}`,
      "",
      ...detail,
      "",
      `TOTAL: $${formatAccounting(totalFiscal)}`,
    ].join("\n");

    try {
      await navigator.clipboard.writeText(costs);
      notification.success({
        className: styles.costNotification,
        message: "Costos copiados",
      });
    } catch {
      notification.error({
        className: styles.costNotification,
        message: "No se pudieron copiar los costos",
      });
    }
  };

  return (
    <Drawer
      className={styles.drawer}
      onClose={() => setOpen(false)}
      open={open}
      title={(
        <>
          C O S T O S
          <DollarTwoTone className={styles.titleIcon} />
        </>
      )}
      width={870}
    >
      <div className={styles.intro}>
        <h2>Captura de costos</h2>
        <p>Agrega conceptos al desglose y define quién cubre cada importe.</p>
      </div>

      <section className={styles.summarySection}>
        <div className={styles.sectionTitle}>Indicadores totales</div>
        <div className={styles.coverageSummary}>
          <div>
            <span>Cobertura tope (iva incl.)</span>
            <strong>{formatCurrency(coberturaTope) || "$0.00"}</strong>
          </div>
          <div>
            <div className={styles.summaryLabel}>
              <span>Escotel</span>
              {totalExcedeCobertura && <p>Excede cobertura tope</p>}
            </div>
            <strong>{formatCurrency(totalesPorPagador.Escotel) || "$0.00"} ({formatCurrency(totalesFiscalesPorPagador.Escotel) || "$0.00"})</strong>
          </div>
          <div>
            <span>Cliente corporativo</span>
            <strong>{formatCurrency(totalesPorPagador["Cliente Corporativo"]) || "$0.00"} ({formatCurrency(totalesFiscalesPorPagador["Cliente Corporativo"]) || "$0.00"})</strong>
          </div>
          <div>
            <span>Usuario final</span>
            <strong>{formatCurrency(totalesPorPagador["Usuario Final"]) || "$0.00"} ({formatCurrency(totalesFiscalesPorPagador["Usuario Final"]) || "$0.00"})</strong>
          </div>
        </div>
      </section>

      <section className={styles.captureSection}>
        <div className={styles.sectionTitle}>Agregar item</div>
        <Form
          className={styles.form}
          form={form}
          initialValues={impuestosIniciales}
          layout="vertical"
          requiredMark={false}
          onFinish={onFinish}
          onValuesChange={onValuesChange}
        >
        <div className={styles.captureGrid}>
          <Form.Item
            label="Tipo de item"
            name="tipoCosto"
            rules={[{required: true, message: "El tipo de item es requerido"}]}
          >
            <Select
              dropdownClassName={styles.selectDropdown}
              options={options}
              placeholder="Selecciona un tipo"
              showSearch
            />
          </Form.Item>

          <Form.Item
            label="Quién paga"
            name="quienPaga"
            rules={[{required: true, message: "Seleccione quién paga"}]}
          >
            <Select
              dropdownClassName={styles.selectDropdown}
              options={opcionesQuienPaga}
              placeholder="Selecciona"
            />
          </Form.Item>

          <Form.Item
            name="cantidad"
            label="Cantidad"
            rules={[
              {required: true, message: "Ingrese la cantidad"},
              {
                validator: (_, value) =>
                  Number(value) > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error("La cantidad debe ser mayor a 0")),
              },
            ]}
          >
            <Input placeholder="0" type="number" min={1} step="any" />
          </Form.Item>

          <Form.Item
            name="costo"
            label="Costo"
            rules={[
              {required: true, message: "Ingrese el costo del item"},
              {
                validator: (_, value) =>
                  Number(value) > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error("El costo debe ser mayor a 0")),
              },
            ]}
          >
            <Input placeholder="0.00" type="number" min={1} step="any" />
          </Form.Item>

          <Form.Item label="IVA" name="iva">
            <Select dropdownClassName={styles.selectDropdown} options={opcionesIVA} />
          </Form.Item>

          <Form.Item label="IVA-R" name="ivaR">
            <Select dropdownClassName={styles.selectDropdown} options={opcionesIVARetenido} />
          </Form.Item>

          <Form.Item label="ISR-R" name="isrR">
            <Select dropdownClassName={styles.selectDropdown} options={opcionesISRRetenido} />
          </Form.Item>
        </div>

        {esClienteCorporativo && (
          <div className={styles.corporateGrid}>
            <Form.Item
              label="Clave autorización"
              name="claveAutorizacion"
              rules={[{required: true, message: "Ingrese la clave de autorización"}]}
            >
              <Input.Password placeholder="Clave autorización" />
            </Form.Item>
            <Form.Item
              label="Evidencia"
              name="file"
              valuePropName="fileList"
              getValueFromEvent={event => event.fileList}
              rules={[{required: true, message: "Seleccione una evidencia"}]}
            >
              <Upload accept="application/pdf,image/*" beforeUpload={() => false} maxCount={1}>
                <Button icon={<UploadOutlined />}>Seleccionar evidencia</Button>
              </Upload>
            </Form.Item>
            <Form.Item label="Quién autoriza" name="quienAutorizaCliente">
              <Input placeholder="Nombre de quien autoriza" />
            </Form.Item>
            <Form.Item label="Comentarios" name="comentarios">
              <Input.TextArea placeholder="Comentarios" />
            </Form.Item>
          </div>
        )}

        {excedeCobertura && (
          <div className={styles.warning}>Excede cobertura tope</div>
        )}

        <div className={styles.formActions}>
          <Button
            className={styles.addButton}
            disabled={camposRequeridosIncompletos || (esClienteCorporativo && (!preview.claveAutorizacion || !preview.file?.length))}
            type="primary"
            htmlType="submit"
            loading={isLoading}
          >
            Agregar costo
          </Button>
        </div>
        </Form>
      </section>

      {items.length > 0 && (
        <section className={styles.itemsSection}>
          <div className={styles.sectionTitle}>Costos registrados</div>
          <div className={styles.itemsList}>
            <div className={styles.itemsHeader}>
              <span>Concepto</span>
              <span>Cantidad</span>
              <span>Unitario</span>
              <span>Subtotal</span>
              <span>IVA</span>
              <span>IVA-R</span>
              <span>ISR-R</span>
              <span>Total</span>
            </div>
            {items.map(costo => {
              const costoConImpuestos = {...impuestosIniciales, ...costo, ...(impuestosPorItem[costo.id] || {})};
              const importes = calcularImportes(costoConImpuestos);

              return (
            <div key={costo.id} className={styles.itemCost}>
              <div>
                <div className={styles.itemHeader}>
                  <p className={styles.itemTitle}>{costo.tipoCosto}</p>
                  {costo.urlDocumento && (
                    <FileImageOutlined
                      className={styles.evidenceIcon}
                    onClick={() => openDocument(costo.urlDocumento)}
                    title="Ver evidencia"
                    />
                  )}
                </div>
                {costo.quienPaga && <span className={styles.itemMeta}>{costo.quienPaga}</span>}
              </div>

              {editingItem?.id === costo.id ? (
                <div className={styles.editFields}>
                  <Input
                    className={styles.quantityInput}
                    min={1}
                    onChange={event => setEditingItem({...editingItem, cantidad: event.target.value})}
                    step="any"
                    type="number"
                    value={editingItem.cantidad}
                  />
                  <Input
                    className={styles.costInput}
                    min={1}
                    onChange={event => setEditingItem({...editingItem, costo: event.target.value})}
                    prefix="$"
                    step="any"
                    type="number"
                    value={editingItem.costo}
                  />
                  <Select
                    onChange={value => setEditingItem({...editingItem, iva: value})}
                    options={opcionesIVA}
                    value={editingItem.iva}
                  />
                  <Select
                    onChange={value => setEditingItem({...editingItem, ivaR: value})}
                    options={opcionesIVARetenido}
                    value={editingItem.ivaR}
                  />
                  <Select
                    onChange={value => setEditingItem({...editingItem, isrR: value})}
                    options={opcionesISRRetenido}
                    value={editingItem.isrR}
                  />
                </div>
              ) : (
                <div className={styles.itemAmount}>
                  <span className={styles.quantity}>{formatAccounting(costo.cantidad)}</span>
                  <span className={`${styles.accountingAmount} ${styles.attachedAmount}`}>
                    <strong>${formatAccounting(costo.costo)}</strong>
                  </span>
                  <span className={`${styles.accountingAmount} ${styles.attachedAmount}`}>
                    <strong>${formatAccounting(importes.subtotal)}</strong>
                  </span>
                  <span className={styles.taxAmount}>
                    <strong>${formatAccounting(importes.importeIVA)}</strong>
                    <span>{formatAccounting(costoConImpuestos.iva)}%</span>
                  </span>
                  <span className={styles.taxAmount}>
                    <strong>${formatAccounting(importes.importeIVARetenido)}</strong>
                    <span>{formatAccounting(costoConImpuestos.ivaR)}%</span>
                  </span>
                  <span className={styles.taxAmount}>
                    <strong>${formatAccounting(importes.importeISRRetenido)}</strong>
                    <span>{formatAccounting(costoConImpuestos.isrR)}%</span>
                  </span>
                  <span className={`${styles.accountingAmount} ${styles.attachedAmount} ${styles.subtotal}`}>
                    <strong>${formatAccounting(importes.totalFiscal)}</strong>
                  </span>
                </div>
              )}

              <Button
                className={styles.editButton}
                icon={editingItem?.id === costo.id ? <SaveOutlined /> : <EditOutlined />}
                loading={isLoading && editingItem?.id === costo.id}
                onClick={() => editingItem?.id === costo.id ? onEdit() : setEditingItem(costoConImpuestos)}
                size="small"
                type="text"
              />

              <Button
                className={styles.deleteButton}
                danger
                icon={<DeleteOutlined />}
                loading={isLoading}
                onClick={() => onDelete(costo.id)}
                size="small"
                type="text"
              />
            </div>
              );
            })}
            <div className={styles.itemsTotal}>
              <Button
                className={styles.copyQuotationButton}
                icon={<CopyOutlined />}
                onClick={copyCosts}
                size="small"
                title="Copiar costos"
                type="text"
              />
              <div className={styles.accountingAmount}>
                <span>$</span>
                <strong>{formatAccounting(totalFiscal)}</strong>
              </div>
            </div>
          </div>
        </section>
      )}

      <Modal
        bodyStyle={{position: "relative", textAlign: "center"}}
        className={styles.evidenceModal}
        footer={null}
        onCancel={() => setDocumentUrl("")}
        open={Boolean(documentUrl)}
        title={null}
        width={800}
      >
        <Image
          alt="Evidencia"
          preview={false}
          src={documentUrl}
          style={{maxHeight: "70vh", maxWidth: "100%", objectFit: "contain"}}
        />
        <Button
          className={styles.downloadButton}
          icon={<DownloadOutlined />}
          onClick={() => downloadDocument(documentUrl)}
          title="Descargar"
          type="text"
        />
      </Modal>
    </Drawer>
  );
};
